import { useState, useCallback, useRef } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Upload,
  Download,
  Trash2,
  Plus,
  AlertCircle,
  Loader2,
  Maximize,
  Crop,
  Scaling,
  Check,
  X
} from 'lucide-react'

interface Preset {
  name: string
  nameKey: string
  width: number
  height: number
  icon?: string
}

interface ImageFile {
  id: string
  file: File
  name: string
  originalUrl: string
  resizedUrl: string
  originalWidth: number
  originalHeight: number
  status: 'pending' | 'processing' | 'done' | 'error'
  error: string | null
}

const PRESETS: Preset[] = [
  { name: 'Instagram Post', nameKey: 'presetInstaPost', width: 1080, height: 1080 },
  { name: 'Instagram Story', nameKey: 'presetInstaStory', width: 1080, height: 1920 },
  { name: 'Twitter/X Header', nameKey: 'presetTwitterHeader', width: 1500, height: 500 },
  { name: 'Twitter/X Post', nameKey: 'presetTwitterPost', width: 1200, height: 675 },
  { name: 'Facebook Cover', nameKey: 'presetFacebookCover', width: 820, height: 312 },
  { name: 'Facebook Post', nameKey: 'presetFacebookPost', width: 1200, height: 630 },
  { name: 'YouTube Thumbnail', nameKey: 'presetYouTubeThumb', width: 1280, height: 720 },
  { name: 'LinkedIn Banner', nameKey: 'presetLinkedInBanner', width: 1584, height: 396 },
  { name: 'A4 Portrait', nameKey: 'presetA4Portrait', width: 794, height: 1123 },
  { name: 'A4 Landscape', nameKey: 'presetA4Landscape', width: 1123, height: 794 },
  { name: 'HD 1080p', nameKey: 'presetHD', width: 1920, height: 1080 },
  { name: 'Custom', nameKey: 'presetCustom', width: 800, height: 600 },
]

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  return (bytes / 1024).toFixed(1) + ' KB'
}

function resizeImage(
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  mode: 'cover' | 'contain' | 'stretch' | 'fill',
  bgColor: string = '#FFFFFF'
): string {
  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.fillStyle = bgColor
  ctx.fillRect(0, 0, targetWidth, targetHeight)

  const imgRatio = img.width / img.height
  const targetRatio = targetWidth / targetHeight

  let sx = 0, sy = 0, sw = img.width, sh = img.height
  let dx = 0, dy = 0, dw = targetWidth, dh = targetHeight

  switch (mode) {
    case 'cover': {
      if (imgRatio > targetRatio) {
        sw = img.height * targetRatio
        sx = (img.width - sw) / 2
      } else {
        sh = img.width / targetRatio
        sy = (img.height - sh) / 2
      }
      break
    }
    case 'contain': {
      if (imgRatio > targetRatio) {
        dh = targetWidth / imgRatio
        dy = (targetHeight - dh) / 2
      } else {
        dw = targetHeight * imgRatio
        dx = (targetWidth - dw) / 2
      }
      break
    }
    case 'stretch': {
      // Just draw full image to full canvas
      break
    }
    case 'fill': {
      // Same as stretch but with white background already filled
      break
    }
  }

  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)
  return canvas.toDataURL('image/jpeg', 0.92)
}

function App() {
  const { t } = useI18n()
  const [files, setFiles] = useState<ImageFile[]>([])
  const [selectedPreset, setSelectedPreset] = useState(0)
  const [customWidth, setCustomWidth] = useState(800)
  const [customHeight, setCustomHeight] = useState(600)
  const [mode, setMode] = useState<'cover' | 'contain' | 'stretch' | 'fill'>('cover')
  const [bgColor, setBgColor] = useState('#FFFFFF')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const currentPreset = PRESETS[selectedPreset]
  const targetWidth = currentPreset.name === 'Custom' ? customWidth : currentPreset.width
  const targetHeight = currentPreset.name === 'Custom' ? customHeight : currentPreset.height

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return
    setError('')

    const imageFiles = Array.from(newFiles).filter(f => f.type.startsWith('image/'))
    if (imageFiles.length === 0) {
      setError(t('imgResizer.invalidFile') || 'Please select image files')
      return
    }

    const processed: ImageFile[] = imageFiles.map(file => ({
      id: Math.random().toString(36).substring(2, 15),
      file,
      name: file.name,
      originalUrl: URL.createObjectURL(file),
      resizedUrl: '',
      originalWidth: 0,
      originalHeight: 0,
      status: 'pending',
      error: null,
    }))

    setFiles(prev => [...prev, ...processed])
  }, [t])

  const processAll = useCallback(() => {
    if (files.length === 0) return
    const pending = files.filter(f => f.status === 'pending')
    if (pending.length === 0) return

    setIsProcessing(true)
    setError('')

    pending.forEach(item => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)

        const tw = currentPreset.name === 'Custom' ? customWidth : currentPreset.width
        const th = currentPreset.name === 'Custom' ? customHeight : currentPreset.height

        try {
          const dataUrl = resizeImage(img, tw, th, mode, bgColor)
          setFiles(prev => prev.map(f =>
            f.id === item.id
              ? {
                  ...f,
                  status: 'done',
                  resizedUrl: dataUrl,
                  originalWidth: img.width,
                  originalHeight: img.height,
                }
              : f
          ))
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          setFiles(prev => prev.map(f =>
            f.id === item.id
              ? { ...f, status: 'error', error: msg }
              : f
          ))
        }
      }
      img.onerror = () => {
        setFiles(prev => prev.map(f =>
          f.id === item.id
            ? { ...f, status: 'error', error: 'Failed to load image' }
            : f
        ))
      }

      setFiles(prev => prev.map(f =>
        f.id === item.id ? { ...f, status: 'processing' } : f
      ))

      img.src = item.originalUrl
    })

    setIsProcessing(false)
  }, [files, currentPreset, customWidth, customHeight, mode, bgColor])

  const downloadImage = useCallback((item: ImageFile) => {
    if (!item.resizedUrl) return
    const a = document.createElement('a')
    a.href = item.resizedUrl
    const baseName = item.file.name.replace(/\.[^/.]+$/, '')
    a.download = `${baseName}_resized_${targetWidth}x${targetHeight}.jpg`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [targetWidth, targetHeight])

  const removeFile = useCallback((id: string) => {
    setFiles(prev => {
      const item = prev.find(f => f.id === id)
      if (item) {
        if (item.originalUrl) URL.revokeObjectURL(item.originalUrl)
      }
      return prev.filter(f => f.id !== id)
    })
  }, [])

  const clearAll = useCallback(() => {
    files.forEach(f => {
      if (f.originalUrl) URL.revokeObjectURL(f.originalUrl)
    })
    setFiles([])
    setError('')
  }, [files])

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('imgResizer.title') || 'Image Resizer'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('imgResizer.subtitle') || 'Resize images for social media, presentations, and web. '}
          <span className="text-primary font-medium">{t('imgResizer.privacy') || '100% client-side'}</span>, {t('imgResizer.noUpload') || 'no upload needed'}.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Settings Panel */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
          {/* Presets */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-2">
              <Maximize className="w-4 h-4 text-brand-primary" />
              {t('imgResizer.preset') || 'Preset Size'}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {PRESETS.map((preset, idx) => (
                <button
                  key={preset.name}
                  onClick={() => setSelectedPreset(idx)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium text-left transition-colors ${
                    selectedPreset === idx
                      ? 'bg-brand-primary text-white'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  <div className="font-semibold">{t(preset.nameKey) || preset.name}</div>
                  <div className="opacity-70 mt-0.5">{preset.width} × {preset.height}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Size */}
          {currentPreset.name === 'Custom' && (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground block mb-1">{t('imgResizer.width') || 'Width'} (px)</label>
                <input
                  type="number"
                  min={1}
                  max={4000}
                  value={customWidth}
                  onChange={e => setCustomWidth(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
              <span className="text-muted-foreground pt-5">×</span>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground block mb-1">{t('imgResizer.height') || 'Height'} (px)</label>
                <input
                  type="number"
                  min={1}
                  max={4000}
                  value={customHeight}
                  onChange={e => setCustomHeight(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
            </div>
          )}

          {/* Resize Mode */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-2">
              <Crop className="w-4 h-4 text-brand-primary" />
              {t('imgResizer.mode') || 'Resize Mode'}
            </label>
            <div className="flex flex-wrap gap-2">
              {(['cover', 'contain', 'stretch', 'fill'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    mode === m
                      ? 'bg-brand-primary text-white'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {t(`imgResizer.mode${m.charAt(0).toUpperCase() + m.slice(1)}`) || m}
                </button>
              ))}
            </div>
          </div>

          {/* Background Color (for contain/fill) */}
          {(mode === 'contain' || mode === 'fill') && (
            <div className="flex items-center gap-3">
              <label className="text-sm text-foreground">{t('imgResizer.bgColor') || 'Background'}</label>
              <input
                type="color"
                value={bgColor}
                onChange={e => setBgColor(e.target.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer"
              />
              <span className="text-sm font-mono text-muted-foreground">{bgColor}</span>
            </div>
          )}
        </div>

        {/* Upload Area */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault()
            handleFiles(e.dataTransfer.files)
          }}
          className="bg-surface rounded-xl shadow-sm border border-border p-8 md:p-12 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <Upload className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('imgResizer.uploadTitle') || 'Upload Images'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('imgResizer.uploadDesc') || 'Drag & drop or click to select. Supports JPEG, PNG, WebP.'}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('imgResizer.addFiles') || 'Add Images'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => handleFiles(e.target.files)}
            />
            {files.length > 0 && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" />
                {t('actions.clear') || 'Clear All'}
              </button>
            )}
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
            {/* Summary */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border">
              <div className="text-sm">
                <span className="font-medium text-foreground">{files.length}</span>
                <span className="text-muted-foreground"> {t('imgResizer.files') || 'files'}</span>
              </div>
              <button
                onClick={processAll}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('imgResizer.processing') || 'Processing...'}
                  </>
                ) : (
                  <>
                    <Scaling className="w-4 h-4" />
                    {t('imgResizer.resize') || 'Resize All'}
                  </>
                )}
              </button>
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {files.map(item => (
                <div
                  key={item.id}
                  className={`border rounded-lg overflow-hidden ${
                    item.status === 'error'
                      ? 'border-red-200 dark:border-red-800'
                      : item.status === 'done'
                        ? 'border-green-200 dark:border-green-800'
                        : 'border-border'
                  }`}
                >
                  {/* Image Preview */}
                  <div className="aspect-video bg-black/5 dark:bg-white/5 relative overflow-hidden flex items-center justify-center">
                    {item.status === 'done' && item.resizedUrl ? (
                      <img
                        src={item.resizedUrl}
                        alt="Resized"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <img
                        src={item.originalUrl}
                        alt="Original"
                        className={`w-full h-full object-contain ${item.status === 'processing' ? 'opacity-50' : ''}`}
                      />
                    )}
                    {item.status === 'processing' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                      </div>
                    )}
                    {item.status === 'done' && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {targetWidth}×{targetHeight}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3 space-y-2">
                    <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatSize(item.file.size)}</span>
                      {item.originalWidth > 0 && (
                        <span>{item.originalWidth}×{item.originalHeight} → {targetWidth}×{targetHeight}</span>
                      )}
                    </div>

                    {item.status === 'error' && (
                      <p className="text-xs text-red-500">{item.error}</p>
                    )}

                    <div className="flex gap-2 pt-1">
                      {item.status === 'done' && (
                        <button
                          onClick={() => downloadImage(item)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-brand-primary text-white rounded-md hover:opacity-90 transition-opacity text-xs font-medium"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {t('actions.download') || 'Download'}
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(item.id)}
                        className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
